import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Copy, RefreshCw, Send, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/extranet/StatusBadge";
import IcalPanel from "@/components/extranet/IcalPanel";
import ChannexPanel from "@/components/extranet/ChannexPanel";
import IcalExportPanel from "@/components/extranet/IcalExportPanel";
import { http, API, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";

const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Disalin"); };

const KeyRow = ({ label, value, testid }) => (
  <div><Label>{label}</Label>
    <div className="mt-1.5 flex gap-2">
      <Input readOnly value={value} data-testid={testid} className="font-mono-x text-xs" />
      <button data-testid={`${testid}-copy`} onClick={() => copy(value)} className="rounded-md border border-slate-300 px-3 hover:bg-slate-50"><Copy className="h-4 w-4" /></button>
    </div>
  </div>
);

const ENDPOINTS = [
  ["GET", "/property", "Info properti & tipe kamar"], ["GET", "/ari?start=&end=", "Tarif & ketersediaan"],
  ["POST", "/ari", "Update tarif/kuota/tutup"], ["GET", "/reservations?updated_since=", "Tarik reservasi"],
  ["POST", "/reservations", "Kirim reservasi OTA"], ["POST", "/reservations/{code}/cancel", "Batalkan reservasi"],
];

const STATUS_C = { success: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-700", skipped: "bg-slate-200 text-slate-700" };

const Logs = ({ propertyId, tick }) => {
  const [logs, setLogs] = useState([]);
  const load = () => http.get(`/extranet/properties/${propertyId}/channel/logs`).then((r) => setLogs(r.data));
  useEffect(() => { load(); }, [propertyId, tick]); // eslint-disable-line
  return (
    <Panel title="Log sinkronisasi" testid="sync-logs" action={<button data-testid="logs-refresh" onClick={load} className="rounded p-1.5 hover:bg-slate-100"><RefreshCw className="h-4 w-4" /></button>}>
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#F8FAFC] text-left uppercase text-slate-500"><tr><th className="px-4 py-2">Waktu</th><th>Channel</th><th>Arah</th><th>Event</th><th>Status</th><th className="pr-4">Pesan</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Belum ada aktivitas sinkronisasi</td></tr>}
            {logs.map((l) => (
              <tr key={l.id}>
                <td className="whitespace-nowrap px-4 py-2 font-mono-x">{new Date(l.created_at).toLocaleString("id-ID")}</td>
                <td className="capitalize">{l.channel.replace(/_/g, " ")}</td><td>{l.direction}</td><td className="font-mono-x">{l.event}</td>
                <td><span className={`rounded px-1.5 py-0.5 font-semibold ${STATUS_C[l.status] || ""}`}>{l.status}</span></td>
                <td className="max-w-[360px] truncate pr-4 text-slate-600" title={l.message}>{l.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export default function ChannelManager() {
  const { property } = useOutletContext();
  const t = useT();
  const [s, setS] = useState(null);
  const [urls, setUrls] = useState("");
  const [bal, setBal] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [tick, setTick] = useState(0);

  const apply = (d) => { setS(d); setUrls(d.webhook_urls.join("\n")); setBal({ ...d.bookandlink, api_key: "" }); };
  useEffect(() => {
    http.get(`/extranet/properties/${property.id}/channel`).then((r) => apply(r.data));
    http.get(`/extranet/properties/${property.id}/rooms`).then((r) => setRooms(r.data));
  }, [property.id]);

  const run = async (fn, msg) => { try { const r = await fn(); toast.success(msg); setTick((t) => t + 1); return r; } catch (e) { toast.error(errMsg(e)); } };
  const save = () => run(async () => {
    const { data } = await http.put(`/extranet/properties/${property.id}/channel`, { webhook_urls: urls.split("\n"), bookandlink: bal });
    apply(data);
  }, "Pengaturan channel disimpan");
  const regen = () => window.confirm("Buat ulang API key? Channel manager yang memakai key lama akan terputus.") &&
    run(async () => apply((await http.post(`/extranet/properties/${property.id}/channel/regenerate-key`)).data), "API key baru dibuat");
  const testHook = () => run(() => http.post(`/extranet/properties/${property.id}/channel/test-webhook`), "Webhook tes dikirim, cek log");
  const syncBal = async () => {
    try {
      const { data } = await http.post(`/extranet/properties/${property.id}/channel/bookandlink/sync`, null, { params: { days: 730 } });
      if (data.ok) toast.success(`${data.pushed} data tarif dikirim ke Bookandlink`);
      else toast.warning(`Sinkron tidak terkirim: ${data.message || data.status}`);
      setTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!s || !bal) return <p className="p-10 text-center text-slate-500">Memuat...</p>;
  const setB = (k) => (e) => setBal({ ...bal, [k]: e.target.value });
  return (
    <div className="fade-up flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Channel Manager")}</h1>
        <p className="text-sm text-slate-500">{t("Hubungkan channel manager mana pun lewat Open API + webhook, atau aktifkan adaptor Bookandlink atau Channex.")}</p>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Panel title="Open API (semua channel manager)" testid="open-api-panel"
          action={<button data-testid="regen-key-button" onClick={regen} className="text-sm font-semibold text-[#DC2626] hover:underline">Buat ulang key</button>}>
          <div className="space-y-4 p-5">
            <KeyRow label="Base URL" value={`${API}/channel/v1`} testid="api-base-url" />
            <KeyRow label="API key (header X-API-Key)" value={s.api_key} testid="api-key" />
            <KeyRow label="Webhook secret (verifikasi header X-Signature, HMAC-SHA256)" value={s.webhook_secret} testid="webhook-secret" />
            <div className="rounded-md border border-slate-200">
              {ENDPOINTS.map(([m, p, d]) => (
                <div key={p} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 text-xs last:border-0">
                  <span className={`w-12 rounded px-1.5 py-0.5 text-center font-bold ${m === "GET" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{m}</span>
                  <code className="font-mono-x">{p}</code><span className="ml-auto text-slate-500">{d}</span>
                </div>
              ))}
            </div>
            <div><Label>URL webhook keluar (satu per baris)</Label>
              <Textarea data-testid="webhook-urls-input" value={urls} onChange={(e) => setUrls(e.target.value)} placeholder="https://channel-manager-anda.com/webhook" className="mt-1.5 font-mono-x text-xs" />
              <p className="mt-1 text-xs text-slate-500">Event: reservation.created, reservation.modified, reservation.cancelled, ari.updated</p></div>
            <div className="flex gap-2">
              <button data-testid="channel-save-button" onClick={save} className="rounded-md bg-[#006CE4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052B4]">Simpan</button>
              <button data-testid="test-webhook-button" onClick={testHook} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50"><Send className="h-4 w-4" /> Tes webhook</button>
            </div>
          </div>
        </Panel>
        <Panel title="Bookandlink (ChannelKu)" testid="bookandlink-panel"
          action={<label className="flex items-center gap-2 text-sm font-medium">Aktif <Switch data-testid="bal-enabled-switch" checked={bal.enabled} onCheckedChange={(v) => setBal({ ...bal, enabled: v })} /></label>}>
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>URL API Bookandlink</Label><Input data-testid="bal-api-url" value={bal.api_url} onChange={setB("api_url")} placeholder="URL dari akun server Bookandlink" className="mt-1.5" /></div>
            <div><Label>Username</Label><Input data-testid="bal-username" value={bal.username} onChange={setB("username")} className="mt-1.5" /></div>
            <div><Label>Hotel ID</Label><Input data-testid="bal-hotel-id" value={bal.hotel_id} onChange={setB("hotel_id")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>API key</Label><Input data-testid="bal-api-key" type="password" value={bal.api_key} onChange={setB("api_key")} placeholder={bal.has_api_key ? "•••••• tersimpan (isi untuk mengganti)" : "Belum diisi"} className="mt-1.5" /></div>
            <div className="sm:col-span-2">
              <Label>Pemetaan kamar → kode kamar Bookandlink</Label>
              <div className="mt-1.5 space-y-2">
                {rooms.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 text-sm">
                    <span className="w-40 shrink-0 truncate">{r.name}</span>
                    <Input data-testid={`bal-map-${r.id}`} value={bal.room_mapping?.[r.id] || ""} placeholder="Kode kamar"
                      onChange={(e) => setBal({ ...bal, room_mapping: { ...bal.room_mapping, [r.id]: e.target.value } })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <button data-testid="bal-save-button" onClick={save} className="rounded-md bg-[#006CE4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052B4]">Simpan</button>
              <button data-testid="bal-sync-button" onClick={syncBal} disabled={!s.bookandlink.enabled} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-40"><UploadCloud className="h-4 w-4" /> Sinkron 2 tahun</button>
            </div>
            <p className="text-xs text-slate-500 sm:col-span-2">Bookandlink juga bisa mengirim reservasi & update tarif ke Open API di sebelah memakai API key properti ini.</p>
          </div>
        </Panel>
        <ChannexPanel key={`cx-${s.property_id}`} propertyId={property.id} rooms={rooms} initial={s.channex}
          onSaved={() => http.get(`/extranet/properties/${property.id}/channel`).then((r) => apply(r.data))}
          onDone={() => { setTick((x) => x + 1); http.get(`/extranet/properties/${property.id}/channel`).then((r) => apply(r.data)); }} />
        <IcalPanel key={s.property_id} propertyId={property.id} rooms={rooms} initial={s.ical} onDone={() => setTick((x) => x + 1)} />
        <IcalExportPanel propertyId={property.id} rooms={rooms} />
      </div>
      <Logs propertyId={property.id} tick={tick} />
    </div>
  );
}
